import { Box, Button, Collapse, Divider, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Product } from "component/MarketPlace/ProductCard/types";
import React, { ComponentProps, createContext, useContext } from "react";
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import { TruncatedText } from "component/MarketPlace/Typography/Typography.style";

const CompareTableContext = createContext({ hideUnavailable: false });
const CompareTableContainer = ({ hideUnavailable, ...props }: ComponentProps<typeof Box> & { hideUnavailable: boolean }) => {
  return (
    <CompareTableContext.Provider value={{ hideUnavailable }}>
      <Box {...props} />
    </CompareTableContext.Provider>
  );
};

const CompareRow = ({header,data}:{header:string; data:any[];}) => {
  const context = useContext(CompareTableContext);
  if (context.hideUnavailable && data.every(value => !value)) return null;
  const mapValue = (value: string) => {
    return value && value !== 'null' ? value : "--";
  }
  return(
    <TableRow>
      <TableCell sx={{padding:"6px 16px 6px 16px", backgroundColor:"action.hover", maxWidth:{ xs: '150px', sm: '300px'}, width: { xs: '150px', sm: '300px'}, "& p":{display:"block", whiteSpace:'nowrap'}}}>
        <Typography variant="body2" gutterBottom={false} sx={{fontWeight:"bold", textTransform:"uppercase"}}>{header}</Typography>
        <TruncatedText maxlines={1} variant="body2" gutterBottom={false}>{mapValue(data[0])}</TruncatedText>
      </TableCell>
      <TableCell sx={{padding:"26px 16px 6px 16px", verticalAlign: "bottom", width:{ xs: '150px', sm: '300px'}, "& p":{display:"block"}}}>
        <TruncatedText maxlines={1} variant="body2" gutterBottom={false}>{mapValue(data[1])}</TruncatedText>
      </TableCell>
      <TableCell sx={{padding:"26px 16px 6px 16px", verticalAlign: "bottom", width:{ xs: '150px', sm: '300px'}, display: { xs: 'none', sm: 'none', md: 'table-cell'}, "& p":{display:"block", whiteSpace:'nowrap'}}}>
        <TruncatedText maxlines={1} variant="body2" gutterBottom={false}>{mapValue(data[2])}</TruncatedText>
      </TableCell>
      <TableCell sx={{padding:"26px 16px 6px 16px", verticalAlign: "bottom", width:{ xs: '150px', sm: '300px'}, display: { xs: 'none', sm: 'none', md: 'none', lg: 'table-cell' }, "& p":{display:"block", whiteSpace:'nowrap'}}}>
        <TruncatedText maxlines={1} variant="body2" gutterBottom={false}>{mapValue(data[3])}</TruncatedText>
      </TableCell>
    </TableRow>
  );
}

const ProductComapreTable = ({
  currentProduct,
  compareProductList,
  hideUnavailable = false,
  showAll = false,
  locale
}: {
  currentProduct: Product;
  compareProductList: Product[];
  hideUnavailable?: boolean;
  showAll?: boolean;
  locale: any;
}) => {
  const [showMore, setShowMore] = React.useState(showAll);

  const handleShowMore = () => {
    setShowMore(true);
  };

  const ShowMoreButton = () => (<Button sx={{bottom:"90px"}} onClick={handleShowMore} variant="text" size="medium" startIcon={<KeyboardDoubleArrowDown/>} endIcon={<KeyboardDoubleArrowDown/>}>{locale?.productDetail?.showMore}</Button>);

  const getSpecifications = (type: string, spec?: string) => {
    if (!spec) return [currentProduct?.[type], ...compareProductList.map((item) => item?.[type])];
    return [currentProduct?.specifications?.[type]?.[`specifications.${spec}`], ...compareProductList.map((item) => item?.specifications?.[type]?.[`specifications.${spec}`])];
  };

  const processorType = getSpecifications('Processor', 'processor_type');
  const processorSpeed = getSpecifications('Processor', 'processor_speed');

  const displaySize = getSpecifications('Display', 'display_size');
  const displayType = getSpecifications('Display', 'display_type');
  
  const memory = getSpecifications('Memory & Storage', 'memory');
  const storage = getSpecifications('Memory & Storage', 'storage');

  const power = getSpecifications('Power', 'power_adapter');
  const graphic = getSpecifications('Graphic', 'graphic_processor');

  const type = getSpecifications('type');
  const language = getSpecifications('language');
  
  return (
    <CompareTableContainer hideUnavailable={hideUnavailable} display="flex" flexDirection="column" justifyContent="center">
      <Collapse in={showMore} collapsedSize="332px"> 
        <TableContainer>
          {/* Processor */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left" colSpan={4}>
                  <Typography variant="h6">{locale?.productDetail?.processor}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <CompareRow header={locale?.productDetail?.type} data={processorType} />
              <CompareRow header={locale?.productDetail?.speed} data={processorSpeed} />
            </TableBody>
          </Table>
          {/* Diaplay */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left" colSpan={4}>
                  <Typography variant="h6">{locale?.productDetail?.display}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <CompareRow header={locale?.productDetail?.size} data={displaySize} />
              <CompareRow header={locale?.productDetail?.type} data={displayType} />
            </TableBody>
          </Table>
          {/* Memory & Storage */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left" colSpan={4}>
                  <Typography variant="h6">{locale?.productDetail?.memoryStorage}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <CompareRow header={locale?.productDetail?.memory} data={memory} />
              <CompareRow header={locale?.productDetail?.storage} data={storage} />
            </TableBody>
          </Table>
          {/* General */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left" colSpan={4}>
                  <Typography variant="h6">{locale?.productDetail?.general}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <CompareRow header={locale?.productDetail?.approval} data={[...Array(4)]} />
              <CompareRow header={locale?.productDetail?.graphicCard} data={graphic} />
              <CompareRow header={locale?.productDetail?.powerAdapter} data={power} />
              <CompareRow header={locale?.productDetail?.communications} data={[...Array(4)]} />
              <CompareRow header={locale?.productDetail?.warranty} data={[...Array(4)]} />
              <CompareRow header={locale?.productDetail?.language} data={language} />
            </TableBody>
          </Table>
          {/* Popularity */}
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align="left" colSpan={4}>
                  <Typography variant="h6">{locale?.productDetail?.popularity}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <CompareRow header={locale?.productDetail?.type} data={type} />
            </TableBody>
          </Table>
        </TableContainer>
      </Collapse>
      {!showMore && (<>
        <Box
          sx={{
            width: "100%",
            height: "64px",
            background:
              "linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #FFFFFF 50%, #FFFFFF 100%)",
            position: "relative",
            bottom: "64px",
          }}
        ></Box>
        <ShowMoreButton/>
        <Divider sx={{bottom:"90px",position:"relative"}}/>
      </>
      )}
    </CompareTableContainer>
  );
};

export default ProductComapreTable;
